import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Check, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const OPTIONS = [["cagnotte", "Cagnottes entre proches"], ["compte", "Compte en ligne + carte"]];

const Privacy = () => (
  <details className="group text-xs text-muted-foreground" data-testid="waitlist-privacy">
    <summary className="flex cursor-pointer list-none items-center gap-1 font-semibold">Vos données et vos droits <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" /></summary>
    <p className="mt-2 leading-relaxed">CASHMY utilise votre email uniquement pour vous prévenir du lancement des fonctionnalités choisies. Il n'est ni revendu ni partagé. Il est conservé jusqu'au lancement, puis supprimé si vous ne créez pas de compte. Conformément au RGPD, vous pouvez à tout moment vous retirer de la liste depuis cet écran (une fois connecté) ou en demandant la suppression de vos données à l'équipe CASHMY.</p>
  </details>
);

const Joined = ({ interests, onLeave }) => (
  <div className="space-y-3" data-testid="waitlist-joined">
    <p className="flex items-center gap-2 font-bold text-success"><Check className="h-5 w-5" /> Vous êtes sur la liste d'attente</p>
    <p className="text-sm text-muted-foreground">Nous vous préviendrons en priorité pour : {OPTIONS.filter(([k]) => interests.includes(k)).map(([, l]) => l).join(" et ")}.</p>
    <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={onLeave} data-testid="waitlist-leave">Me retirer de la liste et supprimer mes données</Button>
  </div>
);

export const WaitlistForm = ({ defaultInterest }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["waitlist-me"], queryFn: () => api.get("/waitlist/me").then((r) => r.data), enabled: !!user });
  const [email, setEmail] = useState(user?.email || "");
  const [interests, setInterests] = useState([defaultInterest]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const toggle = (k) => setInterests((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/waitlist", { email, interests, consent }); toast.success("Inscription confirmée"); setDone(true); qc.invalidateQueries({ queryKey: ["waitlist-me"] }); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const leave = async () => { await api.delete("/waitlist/me"); toast.success("Vous avez été retiré de la liste, vos données sont supprimées"); setDone(false); qc.invalidateQueries({ queryKey: ["waitlist-me"] }); };
  const joined = me.data?.interests?.length ? me.data.interests : done && !user ? interests : null;
  return (
    <section className="card-surface space-y-4 p-6" data-testid="waitlist-card">
      <h2 className="flex items-center gap-2 text-lg font-extrabold"><BellRing className="h-5 w-5 text-primary" /> Être prévenu du lancement</h2>
      {joined ? <Joined interests={joined} onLeave={user ? leave : null} /> : (
        <form onSubmit={submit} className="space-y-4" data-testid="waitlist-form">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} readOnly={!!user} placeholder="Votre email" className="h-11 read-only:bg-muted" data-testid="waitlist-email" />
          <div className="flex flex-wrap gap-2">{OPTIONS.map(([k, l]) => (
            <button type="button" key={k} onClick={() => toggle(k)} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${interests.includes(k) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"}`} data-testid={`waitlist-interest-${k}`}>{interests.includes(k) && <Check className="h-3.5 w-3.5" />}{l}</button>))}</div>
          <label className="flex items-start gap-2 text-sm"><Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="waitlist-consent" /> J'accepte que CASHMY m'écrive pour me prévenir du lancement. Désinscription possible à tout moment.</label>
          <Button type="submit" className="h-11 w-full rounded-full" disabled={busy || !consent || !interests.length} data-testid="waitlist-submit">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Rejoindre la liste d'attente</Button>
          <Privacy />
        </form>)}
    </section>
  );
};
